import { Button } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { formatCurrency } from '../mock-data';
import { PaymentMethod } from '../types';
import {
  computeConsignmentTotal,
  computeCrossSellGross,
  computeTotalToPay,
} from '../wizard-running-total';
import { useWizardStore } from '../wizard-store';

/**
 * Sticky Live Summary Footer (E-105 Story 105-4, ADR-103 I-3).
 *
 * A wizard alján folyamatosan látható ár-összegzés: Bérleti díj + Bizomány
 * + Kaució + Fizetendő + Kiadás gomb. A pultos már a 2. panelben is tudja
 * az árat — nem kell a Summary step-ig görgetni.
 *
 * Invariáns: a `pricing` **mindig** a `useWizardStore().pricing`-ből jön
 * (nem duplikálja a számítást). A `recalcPricing` már reaktívan fut minden
 * mutáció után a store-ban → pure render-subscribe pattern.
 */

interface StickySummaryFooterProps {
  onSubmit: () => void;
  isSubmitting: boolean;
  canSubmit: boolean;
  /**
   * Layout-variáns (ADR-103 Am-3 Inc-3). 'footer' (default): vízszintes alsó sáv
   * (V1 + mobil). 'rail': függőleges jobb-oldali summary-rail (V2 single-page
   * 3-col, a 03/04-v2 mockup vertikális aside-ja). Ugyanaz a store-adat + submit,
   * csak az elrendezés tér el — viselkedés-semleges.
   */
  variant?: 'footer' | 'rail';
}

export function StickySummaryFooter({
  onSubmit,
  isSubmitting,
  canSubmit,
  variant = 'footer',
}: StickySummaryFooterProps) {
  const { t } = useTranslation();
  // 4-lens HOTFIX 2026-04-13 H1+H2: useShallow + useMemo derived values.
  // Was: full-store destructure → re-render on every unrelated mutation; recompute consignmentTotal
  // + crossSellGross on every keystroke in notes textarea.
  const {
    pricing,
    paymentMethod,
    crossSellItems,
    packageClassifiedItems,
    consignmentQuantities,
    loyaltyRedeem,
  } = useWizardStore(
    useShallow(s => ({
      pricing: s.pricing,
      paymentMethod: s.paymentMethod,
      crossSellItems: s.crossSellItems,
      packageClassifiedItems: s.packageClassifiedItems,
      consignmentQuantities: s.consignmentQuantities,
      loyaltyRedeem: s.loyaltyRedeem,
    }))
  );

  // Memoized derived values — only recompute when the inputs actually change
  const consignmentTotal = useMemo(
    () => computeConsignmentTotal(packageClassifiedItems, consignmentQuantities),
    [packageClassifiedItems, consignmentQuantities]
  );

  const crossSell = useMemo(() => computeCrossSellGross(crossSellItems), [crossSellItems]);
  const crossSellGross = crossSell.gross;
  const crossSellNet = crossSell.net;

  // Bérleti díj (kedvezmény után): subtotal - discountTotal (a pricing már számolja)
  const rentalNet = pricing ? pricing.subtotal - pricing.discountTotal : 0;
  // Kaució (csak a rental amount-en, NEM a termékeken — Q-XSL-1)
  const depositTotal = pricing?.depositTotal ?? 0;
  // Fizetendő = rental (ÁFA-val) + cross-sell (ÁFA-val) + bizomány + kaució
  const rentalGross = pricing?.grandTotal ?? 0;
  // v3-parity G1: a running-total SSoT — a loyalty-credit (post-VAT) levonva,
  // konzisztensen a WizardTopBar felső sávval és a SummaryStep fizetendőjével.
  const totalToPay = computeTotalToPay({
    grandTotal: rentalGross,
    depositTotal,
    crossSellGross,
    consignmentTotal,
    loyaltyAmount: Number(loyaltyRedeem?.amount ?? 0),
  });
  const hasAnyData = pricing !== null || consignmentTotal > 0 || crossSellNet > 0;

  // Am-3 Inc-3: a submit-gomb kiemelve, hogy a footer + rail variant közösen használja.
  const submitButton = (
    <Button
      onClick={onSubmit}
      disabled={!canSubmit || isSubmitting}
      aria-disabled={!canSubmit || isSubmitting}
      className={`bg-green-600 hover:bg-green-700 ${variant === 'rail' ? 'w-full' : 'shrink-0'}`}
      data-testid="rental-submit"
    >
      {isSubmitting ? (
        <>
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {paymentMethod === PaymentMethod.CARD
            ? t('rental.wizard.myposTransaction')
            : t('rental.wizard.creatingRental')}
        </>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {t('rental.wizard.finalizeRental')}
        </>
      )}
    </Button>
  );

  // Am-3 Inc-3 'rail' variant: függőleges jobb-oldali summary (a 03/04-v2 mockup
  // vertikális aside-ja). Ugyanaz a store-adat + submit, csak az elrendezés tér el.
  if (variant === 'rail') {
    const rows: Array<{ label: string; value: string; emphasis?: boolean }> = [
      {
        label: t('rental.wizard.summary.rental'),
        value: hasAnyData ? formatCurrency(rentalNet) : '—',
      },
      ...(consignmentTotal > 0
        ? [
            {
              label: t('rental.wizard.summary.consignment'),
              value: formatCurrency(consignmentTotal),
            },
          ]
        : []),
      {
        label: t('rental.wizard.summary.deposit'),
        value: hasAnyData ? formatCurrency(depositTotal) : '—',
      },
      {
        label: t('rental.wizard.summary.total'),
        value: hasAnyData ? formatCurrency(totalToPay) : '—',
        emphasis: true,
      },
    ];
    return (
      <div
        className="rounded-lg border border-border bg-card p-4"
        role="status"
        aria-live="polite"
        aria-atomic="false"
        data-summary-variant="rail"
      >
        <div className="flex flex-col gap-2 text-sm">
          {rows.map(row => (
            <div key={row.label} className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">{row.label}</span>
              <span
                className={
                  row.emphasis
                    ? 'text-base font-bold text-primary tabular-nums'
                    : 'text-sm font-semibold text-foreground tabular-nums'
                }
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4">{submitButton}</div>
      </div>
    );
  }

  return (
    <div
      className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-4 py-3 sm:px-6 lg:px-8"
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      <div className="flex flex-1 gap-4 text-xs">
        <div className="flex-1 min-w-0">
          <div className="text-muted-foreground truncate">{t('rental.wizard.summary.rental')}</div>
          <div className="font-semibold text-sm text-foreground">
            {hasAnyData ? formatCurrency(rentalNet) : '—'}
          </div>
        </div>
        {consignmentTotal > 0 && (
          <div className="flex-1 min-w-0">
            <div className="text-muted-foreground truncate">
              {t('rental.wizard.summary.consignment')}
            </div>
            <div className="font-semibold text-sm text-foreground">
              {formatCurrency(consignmentTotal)}
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-muted-foreground truncate">{t('rental.wizard.summary.deposit')}</div>
          <div className="font-semibold text-sm text-foreground">
            {hasAnyData ? formatCurrency(depositTotal) : '—'}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-muted-foreground truncate">{t('rental.wizard.summary.total')}</div>
          <div className="font-bold text-base text-primary">
            {hasAnyData ? formatCurrency(totalToPay) : '—'}
          </div>
        </div>
      </div>
      {submitButton}
    </div>
  );
}
